import { TaskResponse } from "@9amhealth/openapi";
import { Blac, Cubit } from "blac-next";
import {
  iconUrl9amInjector,
  iconUrlClipboardCheck
} from "src/constants/iconUrls";
import OnboardingBloc from "src/state/OnboardingBloc/OnboardingBloc";
import { StorageController } from "src/state/StorageBloc/StorageBloc";
import TaskManagementBloc, {
  GLP_CONTINUATION_CONSOLIDATED,
  TaskKey,
  TaskObserverEvent,
  TaskResponseKnown
} from "src/state/TaskManagementBloc/TaskManagementBloc";
import { StepStatus } from "../StepList/StepList";

export enum Variant {
  labOrderUnderReview = "labOrderUnderReview",
  labsRequired = "labsRequired",
  labOrderVerified = "labOrderVerified",
  labFileUnderReview = "labFileUnderReview"
}

export interface NotificationCardState {
  variant: Variant | null;
  variantImage: string | null;
  variantImageIsWide: boolean;
  dismissedVariant: Variant | null;
  isVisible: boolean;
}

const STORAGE_KEY = "notificationCard.dismissedVariant";

const initialState: NotificationCardState = {
  variant: null,
  variantImage: null,
  variantImageIsWide: false,
  dismissedVariant: null,
  isVisible: false
};

export default class NotificationCardCubit extends Cubit<NotificationCardState> {
  private taskManagementBloc = Blac.getBloc(TaskManagementBloc);
  private taskObserver: ReturnType<
    typeof this.taskManagementBloc.addObserver
  > | null = null;

  constructor() {
    super(initialState);
    this.init();
  }

  private init = () => {
    const stored = StorageController.getItem(STORAGE_KEY);
    if (stored && Object.values(Variant).includes(stored as Variant)) {
      this.patch({ dismissedVariant: stored as Variant });
    }

    this.taskObserver = this.taskManagementBloc.addObserver(
      TaskObserverEvent.TASK_LIST_CHANGED,
      this.handleTaskUpdate
    );

    this.updateVariant();
  };

  private handleTaskUpdate = () => {
    this.updateVariant();
  };

  private getVariantFromTasks(tasks: TaskResponseKnown[]): Variant | null {
    if (!tasks.length) {
      return null;
    }

    const glpContinuationTask = this.taskManagementBloc.getTask(
      GLP_CONTINUATION_CONSOLIDATED
    );
    const isGlpContinuationActive = Boolean(glpContinuationTask);
    const onboardingBloc = Blac.getBloc(OnboardingBloc);

    const labUploadTask: TaskResponseKnown | undefined = tasks.find(
      (task) => task.slug === TaskKey.UPLOAD_LABS
    );

    /* TO DO Add it back or remove afterFEWEB-2351 has defined correct logic*/
    // if (labOrderTask?.status === TaskResponse.status.COMPLETED) {
    //   return Variant.labOrderVerified;
    // }

    if (
      [StepStatus.IN_PROGRESS, StepStatus.PENDING].includes(
        onboardingBloc.statusStepGetYourLabsDone()
      ) &&
      isGlpContinuationActive
    ) {
      return Variant.labsRequired;
    }

    /* TO DO Add it back after FEWEB-2351 has defined correct logic*/
    // if (labUploadTask?.status === TaskResponse.status.LOCKED) {
    //   return Variant.labOrderUnderReview;
    // }

    if (labUploadTask?.status === TaskResponse.status.IN_PROGRESS) {
      return Variant.labFileUnderReview;
    }

    return null;
  }

  private getVariantImage(variant: Variant | null) {
    switch (variant) {
      case Variant.labOrderUnderReview:
      case Variant.labsRequired:
        return {
          variantImage: iconUrl9amInjector,
          variantImageIsWide: true
        };
      case Variant.labOrderVerified:
      case Variant.labFileUnderReview:
        return {
          variantImage: iconUrlClipboardCheck,
          variantImageIsWide: false
        };
    }

    return {
      variantImage: null,
      variantImageIsWide: false
    };
  }

  updateVariant = () => {
    const uploadLabsTask = this.taskManagementBloc.getTask(TaskKey.UPLOAD_LABS);
    const labOrderTask = this.taskManagementBloc.getTask(
      TaskKey.COMPLETE_INITIAL_LAB_ORDER
    );
    const labTasks = [uploadLabsTask, labOrderTask].filter(
      (task): task is TaskResponseKnown => Boolean(task)
    );
    const newVariant = this.getVariantFromTasks(labTasks);

    const { variantImage, variantImageIsWide } =
      this.getVariantImage(newVariant);

    const { variant: currentVariant, dismissedVariant } = this.state;

    // If variant changed and it's different from the dismissed one, reset dismiss state
    if (newVariant && dismissedVariant && newVariant !== dismissedVariant) {
      StorageController.removeItem(STORAGE_KEY);
      this.patch({
        variant: newVariant,
        variantImage,
        variantImageIsWide,
        dismissedVariant: null,
        isVisible: true
      });
      return;
    }

    const isVisible = Boolean(newVariant && newVariant !== dismissedVariant);

    if (newVariant !== currentVariant || isVisible !== this.state.isVisible) {
      this.patch({
        variant: newVariant,
        variantImage,
        variantImageIsWide,
        isVisible
      });
    }
  };

  dismiss = () => {
    const { variant } = this.state;
    if (variant) {
      StorageController.setItem(STORAGE_KEY, variant);
      this.patch({
        dismissedVariant: variant,
        isVisible: false
      });
    }
  };

  _dispose(): void {
    // @ts-expect-error - taskObserver is not typed
    this.taskManagementBloc.removeObserver(this.taskObserver);
    this.taskObserver = null;
    super._dispose();
  }
}
